/**
 * PDF Download Helper
 *
 * Utility functions for downloading PDF files from the public assets folder
 */

/**
 * Downloads a PDF file from the public assets folder
 * @param filename - The filename of the PDF to download (without path)
 * @param displayName - The name to use for the downloaded file (optional)
 */
export const downloadPDF = async (filename: string, displayName?: string): Promise<void> => {
  try {
    // Construct the full path to the PDF in the public assets folder
    const pdfPath = `/assets/pdfs/${filename}`

    // Fetch the PDF file
    const response = await fetch(pdfPath)

    if (!response.ok) {
      throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`)
    }

    // Get the PDF blob
    const blob = await response.blob()

    // Create a download link
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url

    // Use displayName if provided, otherwise use the original filename
    const downloadName = displayName || filename
    link.download = downloadName

    // Trigger the download
    document.body.appendChild(link)
    link.click()

    // Clean up
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    throw new Error(`Failed to download ${filename}: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

/**
 * Downloads the User Guide PDF
 */
export const downloadUserGuide = async (): Promise<void> => {
  await downloadPDF('user_guide.pdf', 'Interax_User_Guide.pdf')
}

/**
 * Downloads the Introduction Desk PDF
 */
export const downloadIntroductionDesk = async (): Promise<void> => {
  await downloadPDF('introduction_desk.pdf', 'Interax_Introduction_Desk.pdf')
}

/**
 * Downloads the Introduction Material (same as Introduction Desk)
 */
export const downloadIntroductionMaterial = async (): Promise<void> => {
  await downloadPDF('introduction_desk.pdf', 'Interax_Introduction_Material.pdf')
}

/**
 * Generic PDF download with error handling and user feedback
 * @param filename - The PDF filename
 * @param displayName - The display name for the download
 * @param onSuccess - Optional success callback
 * @param onError - Optional error callback
 */
export const downloadPDFWithFeedback = async (
  filename: string,
  displayName?: string,
  onSuccess?: (filename: string) => void,
  onError?: (error: string) => void
): Promise<void> => {
  try {
    await downloadPDF(filename, displayName)
    onSuccess?.(displayName || filename)
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
    onError?.(errorMessage)
  }
}
